/*eslint no-var: [2] */
import PropTypes from 'prop-types';

import React from 'react';
import UserText from '../../user_text.jsx';
import utils from '../../../utils';
import moment from 'moment';
import Reply from './note/reply.jsx';
import { Link } from 'react-router';
import connect from '../../connect';
import { replyTo } from '../../../actions/markup';
import { selectContent } from '../../../selectors/content';
import { selectSection } from '../../../selectors/section';
import { isMine } from '../../../selectors/markup';
import { isInstructorInSection } from '../../../selectors/current_user';

class Note extends React.PureComponent {
    static displayName = 'Note';

    static propTypes = {
        replyTo: PropTypes.func,
        addTag: PropTypes.func.isRequired,
        note: PropTypes.shape({
            id: PropTypes.number,
            note_text: PropTypes.string.isRequired,
            author_name: PropTypes.string.isRequired,
            tags: PropTypes.array.isRequired,
            is_question: PropTypes.bool.isRequired,
            page: PropTypes.number,
            created: PropTypes.string,

            selection_text: PropTypes.string,
            color: PropTypes.string,
            answer: PropTypes.shape({
                id: PropTypes.number,
            }),
        }).isRequired,
        pageLabel: PropTypes.string.isRequired,
        answerIsMine: PropTypes.bool,
        isInstructor: PropTypes.bool.isRequired,
        isMine: PropTypes.bool.isRequired,
        contentId: PropTypes.number.isRequired,
        content: PropTypes.shape({
            title: PropTypes.string.isRequired,
        }).isRequired,
    };

    addTag = (tagName, e) => {
        e.preventDefault();
        this.props.addTag(tagName);
    };

    renderReply = () => {
        if (!this.props.note.is_question) {
            return null;
        }

        if (this.props.note.answer) {
            return (
                <div className="js-reply reply">
                    <Note
                        contentId={this.props.contentId}
                        content={this.props.content}
                        note={this.props.note.answer}
                        pageLabel={this.props.pageLabel}
                        addTag={this.props.addTag}
                        isInstructor={this.props.isInstructor}
                        isMine={this.props.answerIsMine}
                    />
                </div>
            );
        }

        if (this.props.isInstructor) {
            return (
                <div className="js-reply reply">
                    <Reply
                        note={this.props.note}
                        replyTo={this.props.replyTo}
                    />
                </div>
            );
        }
    };

    renderTags = () => {
        const tags = this.props.note.tags;
        if (tags.length === 0) {
            return null;
        }

        const wrappedTags = tags.map(name => {
            const addTag = this.addTag.bind(this, name);
            return (
                <li key={name} className="tag">
                    <button
                        onClick={addTag}
                        onKeyDown={utils.ifSpace(addTag)}
                        className="js-add-tag"
                    >
                        {name}
                    </button>
                </li>
            );
        });

        return (
            <div className="tag-list">
                <span className="tags-label">Tags</span>
                <ul>{wrappedTags}</ul>
            </div>
        );
    };

    renderPrintableTags = () => {
        const tags = this.props.note.tags;
        if (tags.length === 0) {
            return null;
        }
        const wrappedTags = tags.map(name => {
            return <span key={name}>{name}</span>;
        });

        return (
            <div className="printable tags" aria-hidden="true">
                Tags:
                {wrappedTags}
            </div>
        );
    };

    renderEditButton = () => {
        if (this.props.isMine && !this.props.note.answer) {
            const { contentId, pageLabel } = this.props;

            // Don't render a link to a note that hasn't been saved yet.
            if (!this.props.note.id) {
                return false;
            }
            return (
                <Link
                    to={`notes/content-${contentId}/edit/${this.props.note.id}`}
                    className="pull-right cl-icon cl-icon-pencil edit-note-button"
                >
                    <span className="aural">Edit note on page {pageLabel}</span>
                </Link>
            );
        }
    };

    renderSelectionText = () => {
        const { pageLabel, contentId, content } = this.props;
        const { color, selection_text, page: pagenum } = this.props.note;

        if (!selection_text) {
            return null;
        }

        return (
            <div>
                <span className="aural">Highlighted Text. Color: {color}.</span>
                <blockquote className={'selection ' + color}>
                    <span className="color-label">
                        <i className={'annotation ' + color} aria-hidden />
                        <span className="color">{color}</span> highlight on
                        <Link to={`content-${contentId}/address/${pagenum}`}>
                            <span className="aural">Go to </span>
                            Page {pageLabel}{' '}
                            <span className="aural">on {content.title}</span>
                        </Link>
                    </span>
                    <p>{selection_text}</p>
                </blockquote>
                <p className="aural">End highlighted text.</p>
            </div>
        );
    };

    renderTypeLabel = () => {
        if (!this.props.note.is_question) {
            return <span className="annotiation-type">Note</span>;
        }

        return (
            <span className="annotiation-type">
                <span className="cl-icon cl-icon-question-circle" />
                Question
                <span className="aural"> for the instructor:</span>
            </span>
        );
    };

    render() {
        const { author_name, note_text, created } = this.props.note;
        const date_created = moment(created).fromNow();

        return (
            <div className="note clearfix">
                <div className="note-content">
                    <div className="metadata">
                        <div className="author">
                            <h4>
                                <span className="aural">Note Author: </span>
                                {author_name}
                            </h4>
                        </div>
                        <p>
                            <span className="aural">Created: </span>
                            {date_created}
                        </p>
                        {this.renderEditButton()}
                    </div>
                    <span className="aural">Note Content</span>
                    {this.renderSelectionText()}
                    <span className="aural">Annotation Text</span>
                    {this.renderTypeLabel()}
                    <UserText element="div" className="note-text">
                        {note_text}
                    </UserText>

                    {this.renderTags()}
                    {this.renderPrintableTags()}
                    <p className="aural">End of note.</p>
                </div>

                {this.renderReply()}
            </div>
        );
    }
}

function mapStateToProps(state, props) {
    const { content_id: contentId, answer } = props.note;
    const content = selectContent(state, contentId);
    const section = selectSection(state, content.section_id);

    return {
        isInstructor: isInstructorInSection(state, section),
        isMine: isMine(state, props.note),
        answerIsMine: answer != null ? isMine(state, answer) : false,
        contentId,
        content,
    };
}

export default connect(
    mapStateToProps,
    {
        replyTo,
    },
    null,
    { pure: true }
)(Note);



// WEBPACK FOOTER //
// ./src/components/all_notes/chapter/note.jsx